import { Grid, makeStyles, Typography, withTheme } from "@material-ui/core";
import { Dialog, DialogTitle } from "@volt_developers/react-ui-components";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import { ESCAPE_COLON_SETTINGS } from "i18n/utils";

import { ReactComponent as AppleStoreLogo } from "../../../../assets/images/download-on-the-app-store.svg";
import { ReactComponent as GooglePlayLogo } from "../../../../assets/images/get-in-on-google-play.svg";
import { useOperationState } from "../../../../hooks/operationsState";
import { APPLE_STORE_LINK, GOOGLE_STORE_LINK } from "../../constants";
import { useUserSettings } from "../../hooks/useUserSettings";
import { enableTwoFA, getQRCode, operationPath } from "../../store";
import QRCode from "./QRCode";
import { TwoFAEnableForm } from "./TwoFAEnableForm";

import type { TwoFAConfirmationCode } from "../../models/TwoFAConfirmationCode";

interface TwoFaDialogProps {
  isOpen: boolean;
  handleClose: () => void;
}

export function TwoFAEnableDialog({ isOpen, handleClose }: TwoFaDialogProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    clearOperation,
    operationSuccess,
    operationError,
  } = useOperationState(operationPath.enableTwoFA, { defaultLoading: false });
  const { currentUser, userLoading } = useUserSettings();

  const onSubmit = (values: TwoFAConfirmationCode) =>
    dispatch(enableTwoFA(values.code));

  useEffect(() => {
    if (isOpen) {
      clearOperation();
    }
  }, [clearOperation, isOpen]);

  useEffect(() => {
    if (!userLoading && !currentUser?.twoFactorAuthentication && isOpen) {
      dispatch(getQRCode());
    }
  }, [currentUser?.twoFactorAuthentication, dispatch, userLoading, isOpen]);

  useEffect(() => {
    if (operationSuccess) {
      handleClose();
    }
  }, [clearOperation, handleClose, operationError, operationSuccess]);

  return (
    <Dialog
      open={isOpen}
      classes={{ paper: classes.paper }}
      scroll="body"
      onClose={handleClose}
    >
      <DialogTitle onClose={handleClose}>
        {t("Two-factor authentication")}
      </DialogTitle>
      <InfoTypography>
        <Trans t={t}>
          We recommend&nbsp;
          <strong>Google Authenticator</strong>, a Time-based One-Time Password
          (TOTP) app. TOTP apps use a secure algorithm to generate
          authentication codes that change often, increasing your account’s
          security.
        </Trans>
      </InfoTypography>
      <StyledGrid justify="center" alignItems="center" container>
        <Grid item>
          <Link href={GOOGLE_STORE_LINK} target="_blank">
            <GooglePlayLogo />
          </Link>
        </Grid>
        <Grid item>
          <Link href={APPLE_STORE_LINK} target="_blank">
            <AppleStoreLogo />
          </Link>
        </Grid>
      </StyledGrid>
      <TypographyQrWithMargin>
        {t(
          `First, use this QR code to set up your app:`,
          ESCAPE_COLON_SETTINGS
        )}
      </TypographyQrWithMargin>
      <QRCode />
      <TypographyWithMargin>
        {t("Enter the six-digit code from the app:", ESCAPE_COLON_SETTINGS)}
      </TypographyWithMargin>
      <TwoFAEnableForm onSubmit={onSubmit} />
    </Dialog>
  );
}

const useStyles = makeStyles((theme) => ({
  paper: {
    maxWidth: 640,
  },
}));

const StyledTypography = styled(Typography)`
  text-align: center;
  font-size: 16px;
  line-height: 24px;
`;

const InfoTypography = withTheme(styled(StyledTypography)`
  margin-top: ${({ theme }) => `${theme.spacing(2)}px`};
  margin-bottom: ${({ theme }) => `${theme.spacing(4)}px`};
`);

const TypographyQrWithMargin = withTheme(styled(StyledTypography)`
  margin-bottom: 24px;
`);

const TypographyWithMargin = withTheme(styled(StyledTypography)`
  margin-bottom: ${({ theme }) => `${theme.spacing(4)}px`};
`);

const StyledGrid = withTheme(styled(Grid)`
  margin-bottom: ${({ theme }) => `${theme.spacing(4)}px`};
`);

const Link = withTheme(styled.a`
  display: block;
  margin: ${(props) => props.theme.spacing(0, 1)};
`);
